import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackContact } from "../lib/pixel";

/**
 * SPA route-change tracker for Meta Pixel.
 *
 * - Fires `PageView` on every internal route navigation
 *   (initial PageView is already fired by the inline script in index.html)
 * - Globally delegates `Contact` event on any click that opens a WhatsApp link
 *   (any anchor whose href contains "wa.me/")
 *
 * Mount once inside <BrowserRouter>.
 */
const PixelRouteTracker = () => {
    const { pathname, search } = useLocation();

    // Track route changes — skip the very first mount (already counted by base pixel)
    useEffect(() => {
        // Use a module-level flag via window to avoid double-firing on remount.
        if (typeof window === "undefined") return;
        if (window.__trsPixelFirstMount === undefined) {
            window.__trsPixelFirstMount = true;
            return;
        }
        trackPageView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, search]);

    // Delegated click listener for WhatsApp CTAs — mount once
    useEffect(() => {
        if (typeof document === "undefined") return;
        const handler = (e) => {
            const anchor =
                e.target && e.target.closest
                    ? e.target.closest('a[href*="wa.me/"]')
                    : null;
            if (!anchor) return;
            const href = anchor.getAttribute("href") || "";
            const decoded = decodeURIComponent(href);
            const isShare = decoded.includes("https://wa.me/?text=");
            trackContact({
                content_name: isShare
                    ? "WhatsApp Share Referral"
                    : "WhatsApp Reserve",
                source: anchor.getAttribute("data-testid") || "untagged",
            });
        };
        document.addEventListener("click", handler, { capture: true });
        return () =>
            document.removeEventListener("click", handler, { capture: true });
    }, []);

    return null;
};

export default PixelRouteTracker;
